import React from 'react';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { useTranslation } from 'react-i18next';
import ButtonIconMC from './ButtonIconMC';
import StatusTagMC from './StatusTagMC';
import ViewMoreCellMC from './ViewMoreCellMC';
import AvatarMC from './AvatarMC';
import LoadingMC from './LoadingMC';
import SortArrowIcon from './SortArrowIcon';

/*
## Props object for TableMC component

IHeadersProps: Object { ## required
    label: String, ## Column header label text to display in the table header row cell
    key: String, ## Key to access the data in the data array
    info?: String, ## Optional tooltip text to display on hover over the header label
    sortable?: Boolean, ## Optional flag to disable sorting for this specific column (default: true when table sortable is enabled)
    className?: String, ## Optional class name to apply to the header cell
}

IActionsProps: Object {
    label: String, ## Action label text to display in the cell
    action: Function(dataId: String), ## Function to call when the action button is clicked
    icon: String, ## Icon to display in the action button from Heroicons (https://heroicons.com/)
}

IDataCellProps: Object { ## required
    type: 'text' | 'color' | 'status' | 'view_more' | 'status-label' | 'name-initials' | 'country' | 'custom-component' ## Type of data to display in the cell (text, color: '#000000', STATUS)
    value: String | Number | GLOBAL_STATUS.value, ## Value to display in the cell
    info?: String, ## Optional information to display in a tooltip
    className?: String, ## Optional class name to apply to the cell
    onClick?: () => void; // Optional: Function to execute when clicking on "View more"
    limit?: number; // Optional: Character limit to truncate the text
}

IDataRowProps: Object { ## required
    id: String, ## Unique identifier for the row
    [key: String]: IDataCellProps, ## Key that matches the key in the IHeadersProps object to access the data in the row
    actions?: Array<IActionsProps>, ## Optional Array of action objects to display in the cell
    onClick?: () => void, ## Optional row-level click handler. When set, the row becomes keyboard-focusable (role="button", Enter/Space) and shows a hover state.
}

ITableMCProps: Object { ## required
    headers: Array<IHeadersProps> ## Array of header objects to display in the table header row
    data: Array<IDataRowProps> ## Array of data objects to display in the table body rows
    title?: String, ## Optional title to display above the table
    noDataMessage?: String, ## Optional message to display when there is no data in the table
}

props: ITableMCProps

*/

const MemoriedRowCell = React.memo((props) => <div className="tw-h-full">{props.component}</div>);

// TODO: Add min-h-50% to the table to avoid the table to be too small when there is no data and add icon like figma
const TableMC = ({
  tabs,
  hiddeHeaders = false,
  headers,
  data,
  borderAll,
  noDataMessage,
  className = '',
  headerClassName = '',
  cellClassName = '',
  loading,
  secondaryClassName = '',
  emptyComponent,
  tooltipClass = '',
  sortable = false,
  sortColumn = null,
  sortDirection = null,
  onSort = null,
  actionsAlign = 'left',
  actionsHeaderInfo = null
}) => {
  const { t: tCommon } = useTranslation('Common');
  const tableHeaders = headers || [];
  const tableData = data || [];
  const needActionsColumn = tableData.some((row) => row.actions && !!row.actions.length);

  const handleSort = (columnKey) => {
    if (!sortable || !onSort) return;

    let newDirection = 'asc';
    if (sortColumn === columnKey && sortDirection === 'asc') {
      newDirection = 'desc';
    }

    onSort(columnKey, newDirection);
  };

  const getSortIcon = (columnKey) => {
    if (!sortable) return null;

    const isActive = sortColumn === columnKey;

    return (
      <SortArrowIcon
        isActive={isActive}
        direction={sortDirection}
      />
    );
  };

  const isColumnSortable = (header) => {
    return sortable && header.sortable !== false;
  };

  const renderHeaderInfoButton = (key, info) => (
    <>
      <button
        type="button"
        className="tw-flex tw-items-center tw-justify-center tw-w-5 tw-h-5 tw-rounded-full tw-bg-transparent tw-text-gray-400 hover:tw-text-blue-500 tw-transition-colors tw-cursor-pointer tw-border-0 tw-flex-shrink-0 tw-p-0"
        data-tooltip-id={`header-${key}`}
        data-tooltip-content={info}
        aria-label={typeof info === 'string' ? info : 'Column information'}
      >
        <svg className="tw-w-3.5 tw-h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </button>
      <ReactTooltip
        id={`header-${key}`}
        place="top"
        positionStrategy="fixed"
        style={{ maxWidth: '320px', whiteSpace: 'normal', textAlign: 'left', zIndex: 9999 }}
        className={`tw-break-words ${tooltipClass}`}
      />
    </>
  );

  const buildHeaderRowCellByType = (dataItem) => {
    if (!dataItem) return '';

    const isSortable = isColumnSortable(dataItem);
    const sortIcon = getSortIcon(dataItem.key);

    const content = (() => {
      switch (dataItem.type) {
        case 'custom-component':
          return <MemoriedRowCell component={dataItem.label} />;
        default:
          return (
            <div className="tw-flex tw-items-center tw-min-w-0">
              <span className="tw-truncate">
                {dataItem.label}
              </span>
              {isSortable && (
                <div className={`tw-ml-2 tw-flex-shrink-0 ${sortColumn === dataItem.key ? 'tw-text-gray-500' : 'tw-text-gray-400'}`}>
                  {sortIcon}
                </div>
              )}
            </div>
          );
      }
    })();

    const wrappedContent = isSortable ? (
      <button
        type="button"
        className="tw-flex tw-items-center tw-min-w-0 tw-text-left tw-cursor-pointer hover:tw-text-gray-700 tw-transition-colors tw-bg-transparent tw-border-0 tw-p-0"
        onClick={() => handleSort(dataItem.key)}
      >
        {content}
      </button>
    ) : content;

    if (dataItem?.info) {
      return (
        <>
          <div className="tw-flex tw-items-center tw-min-w-0 tw-gap-1.5">
            <div className="tw-flex tw-items-center tw-min-w-0">
              {wrappedContent}
            </div>
            {renderHeaderInfoButton(dataItem.key, dataItem.info)}
          </div>
        </>
      );
    }

    return wrappedContent;
  };

  const buildRowCellByType = (dataItem) => {
    if (!dataItem) return '';
    switch (dataItem.type) {
      case 'status':
        return <StatusTagMC status={dataItem.value} />;
      case 'status_action':
        return <StatusTagMC status={dataItem.value} actions={dataItem.actions || []} />;
      case 'color':
        return (
          <span
            className={`tw-block tw-rounded-full tw-w-5 tw-h-5 tw-text-xs tw-font-medium ${dataItem.value.startsWith('#ffffff') ? 'tw-border' : ''}`}
            style={{ backgroundColor: dataItem.value }}
          />
        );
      case 'view_more':
        return (
          <ViewMoreCellMC
            text={dataItem.value}
            onClick={dataItem.onClick}
            limit={dataItem.limit}
            className={dataItem.className}
          />
        );
      case 'country':
        return <span>{dataItem.value}</span>;
      case 'name-initials':
        return (
          <span className="tw-flex tw-flex-row tw-items-center">
            <span><AvatarMC fullName={dataItem.value} /></span>
            <span className="tw-pl-4">{dataItem.value}</span>
          </span>
        );
      case 'status-label':
        return <span>{dataItem.value}</span>;
      case 'custom-component':
        return <MemoriedRowCell component={dataItem.value} />;
      default:
        return dataItem.value;
    }
  };

  const cellClasses = [
    'tw-py-2.5',
    'tw-px-4',
    'tw-text-default',
    'tw-text-gray-950',
    'tw-font-normal',
    'first:tw-pl-5',
    'last:tw-pr-5',
    cellClassName
  ].join(' ');

  const headerCellClasses = [
    'tw-py-4',
    'tw-px-4',
    'tw-text-default',
    'tw-border-b',
    'tw-text-gray-500',
    'tw-font-normal',
    'tw-font-normal',
    'first:tw-pl-6',
    'first:tw-rounded-ss-xl',
    'last:tw-pr-6',
    'last:tw-rounded-se-xl',
    'tw-truncate',
    headerClassName
  ].join(
    ' '
  );

  const scrollBorderRadiusClass = borderAll ? (tabs ? 'tw-rounded-b-xl' : 'tw-rounded-xl') : '';
  const theadClassName = tabs ? 'tw-bg-gray-50' : 'tw-bg-gray-50 tw-rounded-t-xl';

  return (
    <div className={`tw-wrapper tw-w-full ${borderAll ? 'tw-border tw-rounded-xl tw-overflow-hidden' : ''} ${className}`}>
      {tabs && (tabs)}
      <div className={`tw-overflow-x-auto tw-min-w-0 ${scrollBorderRadiusClass} ${secondaryClassName}`}>
        <table className="tw-table-auto tw-min-w-full tw-w-max">
          {!hiddeHeaders && (
            <thead className={theadClassName}>
              <tr>
                {(tableHeaders || [])?.map((header) => (
                  <th key={header.key} className={`${headerCellClasses} ${header.className || ''}`.trim()}>
                    <div className="tw-flex">
                      {buildHeaderRowCellByType(header)}
                    </div>
                  </th>
                ))}
                {needActionsColumn && (
                  <th className={`${headerCellClasses} tw-whitespace-nowrap tw-w-px ${actionsAlign === 'right' ? 'tw-text-right' : ''}`}>
                    <div className={`tw-flex tw-items-center tw-min-w-0 tw-gap-1.5 ${actionsAlign === 'right' ? 'tw-justify-end' : ''}`}>
                      <span>{tCommon('actions.title')}</span>
                      {actionsHeaderInfo && renderHeaderInfoButton('actions', actionsHeaderInfo)}
                    </div>
                  </th>
                )}
              </tr>
            </thead>
          )}
          <tbody>
            {(!tableData?.length || loading) ? (
              <tr>
                <td colSpan={tableHeaders.length + (needActionsColumn ? 1 : 0)}>
                  <div className="tw-px-6 tw-py-8 tw-text-center tw-w-full">
                    {loading ? (
                      <LoadingMC loading={loading} />
                    ) : (
                      emptyComponent || noDataMessage
                    )}
                  </div>
                </td>
              </tr>
            ) : tableData.map((row, key) => {
              const rowKey = `row-${row.id}-${key}`;
              const cellClass = `${cellClasses} ${key === tableData.length - 1 && borderAll ? '' : 'tw-border-b'}`;
              return (
                <tr
                  key={rowKey}
                  onClick={row.onClick}
                  onKeyDown={row.onClick ? (event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      row.onClick();
                    }
                  } : undefined}
                  role={row.onClick ? 'button' : undefined}
                  tabIndex={row.onClick ? 0 : undefined}
                  className={row.onClick ? 'tw-cursor-pointer hover:tw-bg-gray-50 focus:tw-bg-gray-50 focus:tw-outline-none' : undefined}
                >
                  {tableHeaders?.map((header, headerKey) => {
                    const cellData = row[header.key];
                    const cellKey = `${rowKey}-${header.key}-${headerKey}`;
                    return (
                      <td key={`${cellKey}`} className={`${cellClass} ${cellData?.className || ''}`}>
                        {buildRowCellByType(cellData)}
                        {cellData?.info && (
                          <InfoComponent data={cellData} tooltipClass={tooltipClass} />
                        )}
                      </td>
                    );
                  })}
                  {needActionsColumn && (
                    <td className={`${cellClass} tw-whitespace-nowrap tw-w-px`}>
                      <div className={`tw-flex tw-gap-1 tw-items-center tw-flex-nowrap ${actionsAlign === 'right' ? 'tw-justify-end' : ''}`}>
                        {(row.actions || []).map((action) => (
                          <ButtonIconMC
                            key={`${rowKey}-${action.label}`}
                            icon={action.icon}
                            size={action.iconSize}
                            label={action.label}
                            onClick={(event) => {
                              event.stopPropagation();
                              action.action(row.id);
                            }}
                            disabled={action.disabled}
                            disabledTooltip={action.disabledTooltip}
                            iconClassName="tw-text-gray-700"
                            className="tw-flex tw-items-center tw-justify-center tw-flex-shrink-0"
                          />
                        ))}
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const InfoComponent = ({ data, tooltipClass }) => (
  <ReactTooltip
    place="right"
    type="dark"
    effect="solid"
    id={data.info}
    positionStrategy="fixed"
    style={{ maxWidth: '320px', whiteSpace: 'normal', textAlign: 'left', zIndex: 9999 }}
    className={`tw-break-words ${tooltipClass}`}
  />
);

export default TableMC;
